import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Art, Mascot } from "../../../ui/Art";
import { Icon } from "../../../ui/Icon";
import { Link } from "react-router-dom";
import { quizStars } from "@jomcerdik/shared";
import { api } from "../../../lib/api";
import { t, tk } from "../../../lib/i18n";
import { Card, Stars, levelName, titleCase, useLang } from "../kit";
import { guide } from "../../../world/catalog";

/* ── helpers ─────────────────────────────────────────────────────────────── */
export const rnd = (lo: number, hi: number) => lo + Math.floor(Math.random() * (hi - lo + 1));
export function shuffle<T>(a: readonly T[]): T[] {
  const b = [...a];
  for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j]!, b[i]!]; }
  return b;
}
/** Four distinct options including the answer, shuffled. */
export function options<T>(answer: T, wrong: () => T, key: (x: T) => string = String): T[] {
  const out = [answer];
  for (let guard = 0; out.length < 4 && guard < 200; guard++) { const w = wrong(); if (!out.some((o) => key(o) === key(w))) out.push(w); }
  return shuffle(out);
}

/** Remembered per-browser game level (Tahun 1–6) so maths games start at the right difficulty. */
export function useYear(): [number, (y: number) => void] {
  const [y, setY] = useState(() => { try { return Number(localStorage.getItem("games.year")) || 3; } catch { return 3; } });
  return [y, (v) => { setY(v); try { localStorage.setItem("games.year", String(v)); } catch { /* ignore */ } }];
}

/* ── layout ──────────────────────────────────────────────────────────────── */
export function GameHeader({ title, emoji, tone, score, lives, progress }: { title: string; emoji: string; tone: string; score?: number; lives?: number; progress?: number }) {
  return (
    <div className={`mb-5 rounded-3xl bg-gradient-to-r ${tone} px-4 pb-3 pt-3 text-white shadow-md`}>
      <div className="flex items-center gap-3">
        <Link to="/belajar/permainan" aria-label={t("Tutup")} className="grid h-10 w-10 place-items-center rounded-full bg-white/25 text-lg">✕</Link>
        <p className="flex flex-1 items-center gap-2 truncate text-lg font-extrabold"><GameArt emoji={emoji} className="h-9 w-9" /> {title}</p>
        {lives !== undefined && <span className="flex gap-0.5" role="img" aria-label={t("Nyawa: {n}", { n: lives })}>{[0, 1, 2].map((i) => <Icon key={i} name="heart" className={`h-6 w-6 ${i < lives ? "text-rose-300" : "text-white/30"}`} />)}</span>}
        {score !== undefined && <span className="rounded-full bg-white/25 px-3 py-1 text-sm font-extrabold"><Icon name="star" className="mr-1 inline h-4 w-4 text-amber-300" />{score}</span>}
      </div>
      {progress !== undefined && (
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/25"><div className="h-full rounded-full bg-white transition-all" style={{ width: `${Math.round(progress * 100)}%` }} /></div>
      )}
    </div>
  );
}

/** A short burst of little stars when the child gets one right (drawn, never emoji; the argument is kept for old callers). */
const BURST = ["text-amber-400", "text-pink-400", "text-sky-400", "text-emerald-400"];
export function Burst({ show }: { show: number; emoji?: string[] }) {
  const parts = useMemo(() => Array.from({ length: 12 }, (_, i) => ({
    c: BURST[i % BURST.length]!, dx: `${Math.round(Math.cos((i / 12) * 2 * Math.PI) * rnd(70, 130))}px`, dy: `${Math.round(Math.sin((i / 12) * 2 * Math.PI) * rnd(70, 130))}px`,
  })), [show]);
  if (!show) return null;
  return (
    <div key={show} className="pointer-events-none fixed left-1/2 top-1/2 z-30" aria-hidden>
      {parts.map((p, i) => (
        <span key={i} className="absolute animate-burst" style={{ "--dx": p.dx, "--dy": p.dy } as CSSProperties}><Icon name="star" className={`h-7 w-7 ${p.c}`} /></span>
      ))}
    </div>
  );
}

/** The picture that stands for a game: the drawn icon of that game, never an emoji. */
const GAME_ART: Record<string, string> = { "🚀": "permainan/sifir", "☄️": "permainan/angkasa", "🕒": "permainan/jam", "🍕": "permainan/pecahan", "🪙": "permainan/wang", "🛒": "permainan/wang", "🗺️": "permainan/pengembaraan", "⚡": "permainan/betul-salah", "🃏": "permainan/padan", "🏎️": "permainan/kart", "🏃": "permainan/lari", "🎯": "permainan/lastik", "🪂": "permainan/terbang", "🦖": "permainan/dino", "🌊": "permainan/jetski" };
export function GameArt({ emoji, className = "h-12 w-12" }: { emoji: string; className?: string }) {
  return <Art id={GAME_ART[emoji] ?? "mascot/boy-wave"} alt="" className={className} />;
}

/** Feedback line under a question: "Betul!" or the right answer. */
export function Feedback({ state, right }: { state: "ok" | "no" | null; right?: ReactNode }) {
  useEffect(() => { if (state) guide(state === "ok" ? "correct" : "wrong", state === "ok" ? tk("Betul! Hebat usaha kamu.") : tk("Cuba lagi, kamu boleh!")); }, [state]);
  if (!state) return <div className="h-7" />;
  return state === "ok"
    ? <p className="h-7 animate-rise text-center text-lg font-extrabold text-emerald-600">{t("Betul!")}</p>
    : <p className="h-7 animate-rise text-center text-base font-bold text-rose-600">{t("Jawapan: ")}{right}</p>;
}

/** End-of-round card: stars, message, play again. Saves the result once. */
export function Result({ game, score, max, onAgain, details }: { game: string; score: number; max: number; onAgain: () => void; details?: Record<string, string | number | boolean> }) {
  const saved = useRef(false);
  useEffect(() => {
    if (saved.current || max <= 0) return;
    saved.current = true;
    api(`/api/learn/games/${game}`, { method: "POST", json: { score: Math.min(score, max), max, ...(details ? { details } : {}) } }).catch(() => undefined);
  }, [game, score, max, details]);
  const stars = quizStars(Math.min(score, max), max);
  return (
    <Card className="animate-rise text-center">
      <Mascot pose={stars >= 2 ? "boy-cheer" : stars === 1 ? "girl-happy" : "boy-think"} className="mx-auto h-32 w-32" />
      <p className="mt-2 text-3xl font-extrabold text-slate-900">{score}/{max}</p>
      <Stars n={stars} size="text-3xl" />
      <p className="mt-1 text-sm text-slate-500">{stars >= 2 ? t("Hebat! Teruskan usaha.") : t("Cuba lagi. Anda pasti boleh!")}</p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        <button type="button" onClick={onAgain} className="rounded-full bg-violet-500 px-6 py-2.5 text-sm font-extrabold text-white shadow">{t("Main lagi")}</button>
        <Link to="/belajar/permainan" className="rounded-full bg-card px-6 py-2.5 text-sm font-bold text-slate-700 ring-1 ring-black/10">{t("Permainan lain")}</Link>
      </div>
    </Card>
  );
}

/** Tahun 1–6 chips for the maths games' difficulty. */
export function YearChips({ year, onYear }: { year: number; onYear: (y: number) => void }) {
  return (
    <div className="flex flex-wrap justify-center gap-2" role="group" aria-label={t("Tahap")}>
      {[1, 2, 3, 4, 5, 6].map((y) => (
        <button key={y} type="button" aria-pressed={year === y} onClick={() => onYear(y)}
          className={`rounded-full px-4 py-1.5 text-sm font-bold ${year === y ? "bg-slate-900 text-white" : "bg-card text-slate-600 ring-1 ring-black/10"}`}>
          {levelName("rendah", y)}
        </button>
      ))}
    </div>
  );
}

/** Start screen shared by every game. */
export function Intro({ emoji, title, text, children, onStart, startLabel }: { emoji: string; title: string; text: string; children?: ReactNode; onStart: () => void; startLabel?: string }) {
  return (
    <Card className="animate-rise space-y-4 text-center">
      <div className="animate-bob flex justify-center"><GameArt emoji={emoji} className="h-28 w-28" /></div>
      <h1 className="text-2xl font-extrabold text-slate-900">{title}</h1>
      <p className="text-sm text-slate-500">{text}</p>
      {children}
      <button type="button" onClick={onStart} className="w-full rounded-full bg-emerald-500 py-3 text-base font-extrabold text-white shadow-md">{startLabel ?? t("Mula!")}</button>
    </Card>
  );
}

/* ── question bank for the content games ─────────────────────────────────── */
export type DeckQ = { id: string; difficulty: string; stemMs: string; stemEn: string; optionsMs: string[]; optionsEn: string[]; answer: number; explanationMs: string; explanationEn: string };
type Subj = { versionId: string; subject: string; nameMs: string; nameEn: string; unitsReady: number };
type Unit = { id: string; titleMs: string; titleEn: string; emoji: string | null; ready: boolean };

/** Year → subject → unit (or all units) picker; returns a shuffled deck with answers. */
export function DeckPicker({ onDeck }: { onDeck: (qs: DeckQ[], label: string) => void }) {
  const { lang, pick } = useLang();
  const [year, setYear] = useYear();
  const [subjects, setSubjects] = useState<Subj[] | null>(null);
  const [subject, setSubject] = useState<Subj | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSubjects(null); setSubject(null); setUnits([]);
    api<{ subjects: Subj[] }>(`/api/learn/year/rendah/${year}`).then((r) => setSubjects(r.subjects.filter((s) => s.unitsReady > 0))).catch(() => setSubjects([]));
  }, [year]);
  useEffect(() => {
    setUnits([]);
    if (!subject) return;
    api<{ units: Unit[] }>(`/api/learn/subject/${subject.versionId}`).then((r) => setUnits(r.units.filter((u) => u.ready))).catch(() => setUnits([]));
  }, [subject?.versionId]);

  const subjName = (s: Subj) => (lang === "en" ? s.nameEn || s.nameMs : t(s.nameMs));
  async function load(q: string, label: string) {
    setBusy(true); setError(null);
    try {
      const r = await api<{ questions: DeckQ[] }>(`/api/learn/games/deck?${q}`);
      onDeck(shuffle(r.questions.filter((x) => x.optionsMs.length === 4 || x.optionsEn.length === 4)), label);
    } catch { setError(t("Tiada soalan untuk pilihan ini lagi.")); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <YearChips year={year} onYear={setYear} />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {subjects === null && <p className="col-span-full text-center text-sm text-slate-500">{t("Memuatkan…")}</p>}
        {subjects?.length === 0 && <p className="col-span-full text-center text-sm text-slate-500">{t("Tiada subjek untuk tahun ini lagi.")}</p>}
        {subjects?.map((s) => (
          <button key={s.versionId} type="button" aria-pressed={subject?.versionId === s.versionId} onClick={() => setSubject(s)}
            className={`rounded-2xl px-3 py-3 text-sm font-bold shadow-sm ${subject?.versionId === s.versionId ? "bg-violet-500 text-white" : "bg-card text-slate-700 ring-1 ring-black/5"}`}>
            {subjName(s)}
          </button>
        ))}
      </div>
      {subject && (
        <div className="space-y-2">
          <button type="button" disabled={busy} onClick={() => load(`versionId=${subject.versionId}`, `${subjName(subject)} · ${levelName("rendah", year)}`)}
            className="w-full rounded-full bg-emerald-500 py-3 text-base font-extrabold text-white shadow-md disabled:opacity-50">
            {busy ? "…" : t("Mula — semua unit")}
          </button>
          <p className="text-center text-xs font-bold uppercase tracking-wide text-slate-400">{t("atau pilih satu unit")}</p>
          <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
            {units.map((u) => (
              <button key={u.id} type="button" disabled={busy} onClick={() => load(`topicId=${u.id}`, pick(titleCase(u.titleMs), u.titleEn))}
                className="flex w-full items-center gap-3 rounded-2xl bg-card px-3 py-2 text-left text-sm font-semibold text-slate-700 ring-1 ring-black/5 disabled:opacity-50">
                <Icon name="book" className="h-5 w-5" />
                <span className="truncate">{pick(titleCase(u.titleMs), u.titleEn)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {error && <p className="text-center text-sm font-semibold text-rose-600">{error}</p>}
    </div>
  );
}

/** Content text in the current language. */
export function useQ() {
  const { lang, pick } = useLang();
  const opts = (q: DeckQ) => (lang === "en" && q.optionsEn?.length === 4 ? q.optionsEn : q.optionsMs?.length === 4 ? q.optionsMs : q.optionsEn);
  return { stem: (q: DeckQ) => pick(q.stemMs, q.stemEn), opts, why: (q: DeckQ) => pick(q.explanationMs, q.explanationEn) };
}

